import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Receipt, DollarSign, Clock3, AlertTriangle } from 'lucide-react';
import { formatUsd } from '@/lib/invoicesData';

const Stat = ({ label, value, caption, Icon, tone = 'default', testId }) => {
  const accent = {
    default: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    danger: 'text-[hsl(var(--destructive))]',
  }[tone];
  const bar = {
    default: 'bg-[hsl(var(--primary))]/60',
    success: 'bg-[hsl(var(--success))]/60',
    warning: 'bg-[hsl(var(--warning))]/60',
    danger: 'bg-[hsl(var(--destructive))]/60',
  }[tone];
  return (
    <Card
      className="relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={testId}
    >
      <span aria-hidden className={cn('metallic absolute left-0 top-0 h-full w-[2px]', bar)} />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
            {label}
          </span>
          {Icon ? <Icon size={16} className={cn('opacity-80', accent)} /> : null}
        </div>
        <div
          className="mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums text-foreground"
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {caption ? (
          <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const InvoicesStatsBar = ({ stats }) => {
  const { t } = useTranslation();
  const {
    total = 0,
    collected = 0,
    outstanding = 0,
    overdue = 0,
    counts = {},
  } = stats || {};
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5"
      data-testid="invoices-stats"
    >
      <Stat
        label={t('invoices.stats.total_invoiced')}
        value={formatUsd(total)}
        caption={t('dashboard.kpi_invoiced_hint', { count: counts.all || 0 })}
        Icon={Receipt}
        testId="invoice-stat-total"
      />
      <Stat
        label={t('invoices.stats.collected')}
        value={formatUsd(collected)}
        caption={t('invoices.stats.caption_paid', { count: counts.paid || 0 })}
        Icon={DollarSign}
        tone="success"
        testId="invoice-stat-collected"
      />
      <Stat
        label={t('invoices.stats.outstanding')}
        value={formatUsd(outstanding)}
        caption={t('invoices.stats.caption_open', {
          count:
            (counts.due || 0) + (counts.upcoming || 0) + (counts.overdue || 0),
        })}
        Icon={Clock3}
        tone="warning"
        testId="invoice-stat-outstanding"
      />
      <Stat
        label={t('invoices.stats.overdue')}
        value={formatUsd(overdue)}
        caption={t('invoices.stats.caption_overdue', {
          count: counts.overdue || 0,
        })}
        Icon={AlertTriangle}
        tone="danger"
        testId="invoice-stat-overdue"
      />
    </div>
  );
};

export default InvoicesStatsBar;
